/**
 * useChatRenderState — the pre-return derived-render block lifted out of ChatBox
 * (delegation pattern). Every derivation + useMemo is reproduced VERBATIM:
 * modulePanePlacement, isChatBusy/isEmptyChat, the streaming-stall detection
 * (activeStreamingMessage/hasAssistantStreamingText/isStreamingStalled),
 * shouldShowThinkingTimeline, thinkingTimelineSteps, selectedChatPaneModuleTitle,
 * the modulePaneElement useMemo (with its EXACT dep array), and
 * modulePanePortalTarget.
 *
 * This is a CONTIGUOUS slice of plain derivations + two useMemos, so calling it
 * at the same position the block occupied preserves hook order exactly. It is a
 * .tsx because modulePaneElement renders <ChatModulePane> — the quarantine
 * sub-locus for pane-only @/modules imports. That import already lived in
 * ChatBox and ChatModulePane stays under features/chat, so re-homing the memo
 * here surfaces NO new cross-layer import site. State is owned by the
 * orchestrator and threaded in; refs thread in by reference.
 */
import { useMemo } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { ChatModulePane } from '../components/chatbox/ChatModulePane';
import { STATUS_MESSAGES } from '../components/chatbox/constants';
import type { ModulePanePlacement } from '../components/chatbox/types';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { StreamActivityStep } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import type { EditableModuleTitleRef } from '@/modules/_infrastructure/EditableModuleTitle';

export interface UseChatRenderStateDeps {
  shouldShowAnyPane: boolean;
  isStreaming: boolean;
  isLocalViewIntentThinking: boolean;
  isLoading: boolean;
  messages: ChatMessage[];
  streamActivity: StreamActivityStep[];
  thinking: string | null;
  elapsedSeconds: number;
  selectedChatPaneModule: Module | null;
  shouldShowModulePane: boolean;
  activeConversation: Conversation | null;
  isMobile: boolean;
  isModulePaneFullscreenActive: boolean;
  hideModulePane: () => void;
  moduleMenuCloseSignal: number;
  showIntegrationAccountsModal: boolean;
  showManageAccountsModal: boolean;
  setShowIntegrationAccountsModal: Dispatch<SetStateAction<boolean>>;
  setShowManageAccountsModal: Dispatch<SetStateAction<boolean>>;
  modulePaneCardRef: RefObject<HTMLDivElement | null>;
  modulePaneTitleRef: RefObject<EditableModuleTitleRef | null>;
  chatBoxRootRef: RefObject<HTMLDivElement | null>;
}

export interface UseChatRenderStateResult {
  modulePanePlacement: ModulePanePlacement;
  isChatBusy: boolean;
  isEmptyChat: boolean;
  shouldShowThinkingTimeline: boolean;
  thinkingTimelineSteps: StreamActivityStep[];
  selectedChatPaneModuleTitle: string;
  modulePaneElement: React.ReactElement | null;
  modulePanePortalTarget: HTMLDivElement | null;
}

export function useChatRenderState(deps: UseChatRenderStateDeps): UseChatRenderStateResult {
  const {
    shouldShowAnyPane,
    isStreaming,
    isLocalViewIntentThinking,
    isLoading,
    messages,
    streamActivity,
    thinking,
    elapsedSeconds,
    selectedChatPaneModule,
    shouldShowModulePane,
    activeConversation,
    isMobile,
    isModulePaneFullscreenActive,
    hideModulePane,
    moduleMenuCloseSignal,
    showIntegrationAccountsModal,
    showManageAccountsModal,
    setShowIntegrationAccountsModal,
    setShowManageAccountsModal,
    modulePaneCardRef,
    modulePaneTitleRef,
    chatBoxRootRef,
  } = deps;

  const modulePanePlacement: ModulePanePlacement = !shouldShowAnyPane
    ? 'hidden'
    : 'split';
  const isChatBusy = isStreaming || isLocalViewIntentThinking || isLoading;
  const shouldSuppressEmptyStateForModule = modulePanePlacement !== 'hidden';
  const hasRealMessages = messages.some((m) => !m.inlineModuleId);
  const isEmptyChat = !hasRealMessages && !isChatBusy && !shouldSuppressEmptyStateForModule;
  const activeStreamingMessage = isStreaming ? messages[messages.length - 1] : null;
  const hasAssistantStreamingText = Boolean(
    activeStreamingMessage
    && activeStreamingMessage.role === 'assistant'
    && (activeStreamingMessage as any).streaming
    && String(activeStreamingMessage.content || '').trim().length > 0,
  );
  const lastStreamingChunkAtMs = activeStreamingMessage?.timestamp instanceof Date
    ? activeStreamingMessage.timestamp.getTime()
    : null;
  const isStreamingStalled = Boolean(
    isStreaming
    && hasAssistantStreamingText
    && lastStreamingChunkAtMs
    && (Date.now() - lastStreamingChunkAtMs >= 700),
  );
  const shouldShowThinkingTimeline = isStreaming
    ? streamActivity.length > 0 || Boolean(thinking) || !hasAssistantStreamingText || isStreamingStalled
    : isLocalViewIntentThinking;
  // Single-line status. The hook still appends concrete work statuses to
  // streamActivity (so phrase-dedup + min-display-time both work), but
  // the UI only renders the most recent step — no stacked history, no
  // bulleted timeline. The current step's label animates via
  // AnimatedThinkingLabel downstream.
  const thinkingTimelineSteps = isStreaming && streamActivity.length > 0
    ? [streamActivity[streamActivity.length - 1]]
    : [{
      id: isStreaming ? 'fallback' : 'local-fallback',
      label: isStreaming
        ? thinking || STATUS_MESSAGES[elapsedSeconds % STATUS_MESSAGES.length]
        : STATUS_MESSAGES[0],
      type: 'thinking' as const,
      timestamp: Date.now(),
    }];
  const selectedChatPaneModuleTitle = useMemo(() => {
    if (!selectedChatPaneModule) return '';
    if (selectedChatPaneModule.label && selectedChatPaneModule.label.trim()) {
      return selectedChatPaneModule.label.trim();
    }
    const fallback = selectedChatPaneModule.type || 'Module';
    return fallback
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [selectedChatPaneModule]);
  const modulePaneElement = useMemo(() => {
    if (!activeConversation?.id) return null;

    if (!shouldShowModulePane || !selectedChatPaneModule) return null;

    // QUARANTINE: all pane-only `@/modules` imports live in ChatModulePane.
    // Rendered inside this useMemo so memoization / render timing stays
    // unchanged. The orchestrator keeps every piece of state and threads it
    // as props.
    return (
      <ChatModulePane
        selectedChatPaneModule={selectedChatPaneModule}
        activeConversationId={activeConversation.id}
        isMobile={isMobile}
        modulePaneCardRef={modulePaneCardRef}
        modulePaneTitleRef={modulePaneTitleRef}
        selectedChatPaneModuleTitle={selectedChatPaneModuleTitle}
        hideModulePane={hideModulePane}
        moduleMenuCloseSignal={moduleMenuCloseSignal}
        showIntegrationAccountsModal={showIntegrationAccountsModal}
        showManageAccountsModal={showManageAccountsModal}
        setShowIntegrationAccountsModal={setShowIntegrationAccountsModal}
        setShowManageAccountsModal={setShowManageAccountsModal}
      />
    );
  }, [
    activeConversation?.id,
    hideModulePane,
    isMobile,
    isModulePaneFullscreenActive,
    showIntegrationAccountsModal,
    showManageAccountsModal,
    selectedChatPaneModule,
    shouldShowModulePane,
    selectedChatPaneModuleTitle,
  ]);
  const modulePanePortalTarget = modulePanePlacement === 'split'
    ? chatBoxRootRef.current
    : null;

  return {
    modulePanePlacement,
    isChatBusy,
    isEmptyChat,
    shouldShowThinkingTimeline,
    thinkingTimelineSteps,
    selectedChatPaneModuleTitle,
    modulePaneElement,
    modulePanePortalTarget,
  };
}
